import React, {useMemo, useState, useRef, useCallback, useLayoutEffect} from "react";
import {Box, Stack, Typography, ToggleButtonGroup, ToggleButton, Drawer} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {HelpPopover} from "./cells";
import {PHONE_STACK} from "./HeaderBar";
import {TYPE, TABULAR, NEUTRAL, MONO, HEAT, SEV, heatFor} from "./tokens";
import {DAY_METRICS, fmtSignedMoney, fmtSignedPct, fmtDay, fmtDow, shortProvider, EMPTY, isNilValue, PRESETS} from "./format";

/**
 * Heatmap: every tag painted day by day. Colour rule (tokens.heatFor): the day's value against a base —
 *   < 50 % deep red · < 80 % red · 80–130 % no tint · > 130 % green · > 200 % deep green.
 * `mode === "own"`: base = the tag's own mean over its earning days among the 7 days before that day (all its earning
 * days in the range when fewer than 2). `mode === "d1"`: base = the day before.
 * Grey = nothing sent and nothing earned. Hollow = last day not reported by the partner yet. Dotted = new tag.
 */

export const HEATMAP_HELP = [
    "Each cell is one tag on one day, coloured against its own usual: red when it earned less than 80% of it, deep red under 50%, green above 130%, deep green above 200%.",
    "Switch the base to \"vs day before\" to see day-to-day moves instead of the trend against the 7-day average.",
    "Grey cell = nothing sent and nothing earned that day. Hollow cell = the partner has not reported that day yet.",
    "Dotted cells mark a new tag from its first day. Network rows sum their tags and are tinted with a tighter rule (±12% / ±25%, vs their 7-day median) because sums wobble less; their label shows the last day's move and the tags behind it (3↓ 1↑, or the one tag that dragged it). Three levels: channel (the type of the demand feed that pays the tag: Native, Google search, N2S Yahoo, Yahoo search…) → partner (who pays) → tag; every level is a sum painted with the same tighter rule. Open: none · moved · all folds / unfolds channels and partners.",
    "Click a tag row to open it.",
];

const SHORT_LABEL = {rev: "revenue", net: "net revenue", rpm_fs: "$/1k sent", coverage: "coverage", fs: "sent", rpm: "RPM", ctr: "CTR"};
const ORDER = ["rev", "net", "rpm_fs", "coverage", "ctr", "fs", "rpm"];

/** DAY_METRICS in toolbar order, with the lower-case label used in toggles and the tooltip (CTR = clicks ÷ monetised searches, per day). */
export const HEAT_METRICS = ORDER.map((k) => DAY_METRICS.find((m) => m.key === k)).filter(Boolean)
    .map((m) => ({...m, short: SHORT_LABEL[m.key] || m.label}));

const metricMeta = (key) => HEAT_METRICS.find((m) => m.key === key) || HEAT_METRICS[0];

const MODES = [{key: "own", label: "vs own 7-day avg"}, {key: "d1", label: "vs day before"}];

const NONE = []; // stable default for array props so the model memo holds when the page passes nothing
const GREY_BG = "rgba(0,0,0,.06)";
const HOLLOW_BORDER = "1px dashed rgba(0,0,0,.3)";
const LAUNCH_BORDER = "1px dotted rgba(25,118,210,.6)";
const CELL_H = 20;

// Same look as OverviewBand's toggles (copied: page-local style, no shared module).
const toggleSx = (h = 26) => ({
    "& .MuiToggleButton-root": {
        height: h, px: h < 26 ? 0.75 : 1, fontSize: 11.5, fontWeight: 600, textTransform: "none", lineHeight: 1, ...TABULAR,
        color: "text.secondary", borderColor: NEUTRAL.divider,
        "&.Mui-selected": {color: "primary.main", bgcolor: "rgba(25,118,210,.08)"},
    },
});

const num = (v) => (isNilValue(v) ? null : Number(v));
const mean = (xs) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
const median = (xs) => {
    if (!xs.length) return null;
    const a = [...xs].sort((x, y) => x - y);
    const h = a.length >> 1;
    return a.length % 2 ? a[h] : (a[h - 1] + a[h]) / 2;
};

/**
 * Colour rule for NETWORK rows (sums): a network sum wobbles ~5–6 %/day (a single tag 9–25 %), so ±12 % is the same
 * "2σ" statement the tag rule makes at ±20 %. Deep tints at ±25 % / +35 %. Money metrics also need |Δ| ≥ $25.
 */
const GROUP_MONEY_FLOOR = 25;
const heatGroupFor = (v, base, moneyMetric) => {
    if (v === null || v === undefined || !base || base <= 0) return null;
    if (moneyMetric && Math.abs(Number(v) - base) < GROUP_MONEY_FLOOR) return null;
    const r = Number(v) / base;
    if (r < 0.75) return HEAT.low2;
    if (r < 0.88) return HEAT.low1;
    if (r > 1.35) return HEAT.high2;
    if (r > 1.15) return HEAT.high1;
    return null;
};
const isLow = (bg) => bg === HEAT.low1 || bg === HEAT.low2;
const isHigh = (bg) => bg === HEAT.high1 || bg === HEAT.high2;
const MONEY_METRICS = new Set(["rev", "net"]);

/**
 * Base of a SUM at day `i`: the median of the summed metric over the earning days among the 7 days before (a mean
 * would carry the Sep 10–11 spike into every following day); `d1` mode = the day before.
 */
const sumBase = (daySums, i, m, mode) => {
    const earnKey = m.key === "fs" ? "fs" : "rev";
    if (mode === "d1") {
        const p = daySums[i - 1] ? m.total(daySums[i - 1]) : null;
        return p !== null && p > 0 ? Number(p) : null;
    }
    const prev = [];
    for (let k = Math.max(0, i - 7); k < i; k++) {
        const sm = daySums[k];
        if (sm && sm[earnKey] > 0) { const v = m.total(sm); if (!isNilValue(v)) prev.push(Number(v)); }
    }
    // A sum needs a real history behind it: fewer than 4 earning days before → no base, no tint (never the range's
    // own future days as the reference).
    return prev.length >= 4 ? median(prev) : null;
};
const emptySums = () => ({fs: 0, mon: 0, rev: 0, net: 0, clicks: 0});
const addSums = (s, d) => {
    if (!d) return s;
    s.fs += Number(d.fs) || 0;
    s.mon += Number(d.mon) || 0;
    s.rev += Number(d.rev) || 0;
    s.net += Number(d.net) || 0;   // net = revenue × our share (demand_margin), per day
    s.clicks += Number(d.clicks) || 0;
    return s;
};

// ---------------------------------------------------------------------------------------------------------------------
// Data model: rows → groups → per-cell paint instructions, computed once per (rows, days, metric, mode, …).

/**
 * Base for the cell at `i` in `own` mode: mean of the metric over the "earning" days (rev > 0; fs > 0 for the sent
 * metric) among the 7 days before `i`; when fewer than 2 such days, the mean over all earning days in the range.
 */
const ownBase = (daily, i, m, allEarning) => {
    const earnKey = m.key === "fs" ? "fs" : "rev";
    const prev = [];
    for (let k = Math.max(0, i - 7); k < i; k++) {
        const d = daily[k];
        if (d && Number(d[earnKey]) > 0 && !isNilValue(d[m.key])) prev.push(Number(d[m.key]));
    }
    if (prev.length >= 2) return mean(prev);
    return allEarning.length ? mean(allEarning) : null;
};

const buildRow = (row, days, m, mode, pendingSet, launchFirst) => {
    const daily = Array.isArray(row.daily) ? row.daily : [];
    const earnKey = m.key === "fs" ? "fs" : "rev";
    const allEarning = daily.filter((d) => Number(d[earnKey]) > 0 && !isNilValue(d[m.key])).map((d) => Number(d[m.key]));
    const pending = Boolean(row.partial_last_day) || (row.provider && pendingSet.has(row.provider));
    const first = launchFirst.get(row.tag) || null;
    const sums = emptySums();
    const cells = days.map((day, i) => {
        const d = daily[i] || null;
        addSums(sums, d);
        const v = d ? num(d[m.key]) : null;
        const fs = d ? Number(d.fs) || 0 : 0;
        const rev = d ? Number(d.rev) || 0 : 0;
        const launch = Boolean(first && day >= first);
        if (!d || (fs === 0 && rev === 0)) return {kind: "grey", v, text: "·", bg: GREY_BG, base: null, launch};
        if (i === days.length - 1 && pending) return {kind: "hollow", v, text: v === null ? EMPTY : m.fmt(v), bg: null, base: null, launch};
        let base = null;
        if (mode === "d1") {
            const p = daily[i - 1] ? num(daily[i - 1][m.key]) : null;
            base = p !== null && p > 0 ? p : null;
        } else {
            base = ownBase(daily, i, m, allEarning);
        }
        return {kind: "day", v, text: v === null ? EMPTY : m.fmt(v), bg: heatFor(v, base), base, launch};
    });
    const total = m.total(sums);
    return {
        tag: row.tag, provider: row.provider, site: row.site || null, sev: row._sev || "ok", rev: Number(row.rev) || 0, daily, sums, cells, total,
        totalText: isNilValue(total) ? EMPTY : m.fmt(total),
    };
};

/** Sum a list of built rows into per-day cells (network rule) plus the last-day move signature. */
const summarize = (rowsList, days, m, mode) => {
    const money = MONEY_METRICS.has(m.key);
    const daySums = days.map(emptySums);
    const sums = emptySums();
    rowsList.forEach((r) => {
        days.forEach((day, i) => addSums(daySums[i], r.daily[i]));
        addSums(sums, r.sums);
    });
    const total = m.total(sums);
    const L = days.length - 1;
    const cells = daySums.map((sm, i) => {
        const v = m.total(sm);
        if (sm.fs === 0 && sm.rev === 0) return {kind: "grey", v, text: "·", bg: null, base: null};
        // Never tint a partial sum: while a partner has not reported the day, the cell is hollow.
        if (rowsList.some((r) => r.cells[i] && r.cells[i].kind === "hollow")) return {kind: "hollow", v, text: isNilValue(v) ? EMPTY : m.fmt(v), bg: null, base: null};
        const base = sumBase(daySums, i, m, mode);
        return {kind: "day", v, text: isNilValue(v) ? EMPTY : m.fmt(v), bg: heatGroupFor(v, base, money), base};
    });
    // Last-day move signature: Δ vs base, tags down / up (tag rule), tags that went dark, the one tag that drags the sum.
    const last = cells[L] || null;
    const move = {usd: null, pct: null, down: [], up: [], dark: [], single: null, bg: last ? last.bg : null, kind: last ? last.kind : "grey"};
    if (last && last.kind === "day" && last.base !== null && last.base > 0 && last.v !== null) {
        move.pct = (Number(last.v) / last.base - 1) * 100;
        if (money) move.usd = Number(last.v) - last.base;
    }
    rowsList.forEach((r) => {
        const c = r.cells[L];
        if (!c) return;
        const tagDelta = c.kind === "day" && c.base !== null && c.v !== null ? {tag: r.tag, usd: money ? Number(c.v) - c.base : null, pct: c.base > 0 ? (Number(c.v) / c.base - 1) * 100 : null} : null;
        if (c.kind === "day" && isLow(c.bg)) move.down.push(tagDelta);
        else if (c.kind === "day" && isHigh(c.bg)) move.up.push(tagDelta);
        else if (c.kind === "grey") {
            const earnedBefore = r.daily.slice(Math.max(0, L - 7), L).some((d) => Number(d.rev) > 0);
            if (earnedBefore) { const prev = r.cells[L - 1]; move.dark.push({tag: r.tag, usd: money && prev && prev.v !== null ? -Number(prev.v) : null, pct: -100}); }
        }
    });
    const byLoss = (a, b) => (a.usd !== null && b.usd !== null ? a.usd - b.usd : (a.pct || 0) - (b.pct || 0));
    move.down.sort(byLoss); move.dark.sort(byLoss); move.up.sort((a, b) => -byLoss(a, b));
    if (money && move.usd !== null && Math.abs(move.usd) >= GROUP_MONEY_FLOOR) {
        const cands = [...move.down, ...move.dark, ...move.up].filter((t) => t.usd !== null);
        const top = cands.sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd))[0];
        if (top && Math.abs(top.usd) >= 0.6 * Math.abs(move.usd) && Math.sign(top.usd) === Math.sign(move.usd)) move.single = top;
    }
    return {
        daySums, cells, move, total, totalText: isNilValue(total) ? EMPTY : m.fmt(total), rev: sums.rev, count: rowsList.length,
        alarms: rowsList.filter((r) => r.sev === "error" || r.sev === "warning").length,
        moved: Boolean((last && last.bg) || move.down.length || move.up.length || move.dark.length),
    };
};

const sevRank = (s) => (s === "error" ? 0 : s === "warning" ? 1 : 2);
const OTHER_FAMILY = "Other";

/**
 * Three levels: channel (the demand feed type of the tag's primary feed: Native · Google search · N2S Yahoo…, from the API `row.type_label`) → partner
 * (`row.provider`; the network label when a tag has no partner) → tag. Every level is a sum painted with the network rule.
 */
const buildModel = (rows, days, m, mode, pendingProviders, launches, groupLabel) => {
    const pendingSet = new Set(pendingProviders || []);
    const launchFirst = new Map((launches || []).map((l) => [l.tag, l.first_day]));
    const byFamily = new Map();
    rows.forEach((row) => {
        const family = row.type_label || row.family || OTHER_FAMILY;
        const code = row.type || family;
        const prefix = row.prefix || row.group || "?";
        // "Taboola Deckermedia" → "Deckermedia": the channel row already says Native.
        const partner = row.provider ? shortProvider(row.provider).replace(/^Taboola\s+/i, "") : ((typeof groupLabel === "function" && groupLabel(prefix, row)) || row.group || prefix);
        const key = `${family}|${partner}`;
        if (!byFamily.has(family)) byFamily.set(family, {key: `f:${code}`, family, code, label: family, groups: new Map()});
        const fam = byFamily.get(family);
        if (!fam.groups.has(key)) fam.groups.set(key, {key: `g:${key}`, prefix, family, label: partner, rows: []});
        fam.groups.get(key).rows.push(buildRow(row, days, m, mode, pendingSet, launchFirst));
    });
    const families = Array.from(byFamily.values()).map((fam) => {
        const groups = Array.from(fam.groups.values()).map((g) => {
            g.rows.sort((a, b) => (sevRank(a.sev) - sevRank(b.sev)) || (b.rev - a.rev));
            return {...g, ...summarize(g.rows, days, m, mode)};
        });
        groups.sort((a, b) => ((b.alarms > 0) - (a.alarms > 0)) || (b.rev - a.rev));
        const allRows = groups.flatMap((g) => g.rows);
        return {...fam, groups, ...summarize(allRows, days, m, mode)};
    });
    // Channels by money (positions are learned); Other / Untyped / Unassigned last.
    const tail = (f) => (f.family === "Unassigned" ? 3 : f.family === "Untyped" ? 2 : f.family === OTHER_FAMILY ? 1 : 0);
    families.sort((a, b) => (tail(a) - tail(b)) || (b.rev - a.rev));
    const dayTotals = days.map(emptySums);
    families.forEach((f) => days.forEach((day, i) => addSums(dayTotals[i], f.daySums[i])));
    const grand = emptySums();
    dayTotals.forEach((sm) => addSums(grand, sm));
    const grandTotal = m.total(grand);
    const money = MONEY_METRICS.has(m.key);
    // Level 0: the whole business per day, coloured with the network rule (a sum too).
    const totals = dayTotals.map((sm, i) => {
        const v = m.total(sm);
        const pending = families.some((f) => f.cells[i] && f.cells[i].kind === "hollow");
        const base = pending ? null : sumBase(dayTotals, i, m, mode);
        return {v, text: isNilValue(v) ? EMPTY : m.fmt(v), bg: pending ? null : heatGroupFor(v, base, money), base, pending};
    });
    const groups = families.flatMap((f) => f.groups);
    return {families, groups, totals, dayTotals: totals.map((t) => t.text), grandTotalText: isNilValue(grandTotal) ? EMPTY : m.fmt(grandTotal)};
};

/** `−$31 · 3↓ 1↑` / `−$412 · se67↓` / `+2%` — the folded row's one-line story of the last closed day. */
const moveText = (g, money) => {
    const mv = g.move;
    if (!mv || mv.kind !== "day") return mv && mv.kind === "hollow" ? "◌ pending" : "";
    const head = money ? (mv.usd === null ? "" : fmtSignedMoney(mv.usd)) : (mv.pct === null ? "" : fmtSignedPct(mv.pct));
    const downN = mv.down.length + mv.dark.length;
    let sig = "";
    if (mv.single) sig = `${mv.single.tag}${mv.single.usd < 0 ? "↓" : "↑"}`;
    else if (downN || mv.up.length) sig = `${downN ? `${downN}↓` : ""}${downN && mv.up.length ? " " : ""}${mv.up.length ? `${mv.up.length}↑` : ""}`;
    return [head, sig].filter(Boolean).join(" · ");
};

// ---------------------------------------------------------------------------------------------------------------------
// Cells and rows: plain elements with inline style; events are delegated to the table (no closures per cell).

const cellBase = {
    height: CELL_H, padding: "0 3px", fontSize: 11, lineHeight: `${CELL_H}px`, textAlign: "right", whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums", boxSizing: "border-box", borderBottom: `1px solid ${NEUTRAL.hairline}`,
};

const labelBase = {
    position: "sticky", left: 0, zIndex: 2, textAlign: "left", padding: "0 6px", height: CELL_H + 2, boxSizing: "border-box",
    borderRight: `1px solid ${NEUTRAL.divider}`, borderBottom: `1px solid ${NEUTRAL.hairline}`, fontWeight: 400, verticalAlign: "middle",
};

const TagRow = React.memo(function TagRow({r, minW, labelW, selected, paper, sevColor}) {
    const insets = [];
    if (selected) insets.push(NEUTRAL.selectedInset);
    if (sevColor) insets.push(`inset 2px 0 0 ${sevColor}`);
    // The parent row already names the partner: the tag row carries the site instead.
    const partner = r.site || shortProvider(r.provider);
    const title = `${r.tag} · ${r.site || EMPTY} · ${shortProvider(r.provider)}`;
    return (
        <tr data-tag={r.tag} style={{cursor: "pointer"}} aria-selected={selected || undefined}>
            <th scope="row" role="button" tabIndex={0} data-tag={r.tag} title={title} className="hm-label" style={{
                ...labelBase, width: labelW, minWidth: labelW, maxWidth: labelW, paddingLeft: 22,
                background: selected ? NEUTRAL.selected : paper, boxShadow: insets.join(", ") || undefined,
                backgroundClip: "padding-box",
            }}>
                {/* One line: the grid must show 139 tags without scrolling for ever (22 px rows). */}
                <div style={{display: "flex", alignItems: "baseline", gap: 6, overflow: "hidden", whiteSpace: "nowrap", lineHeight: "18px"}}>
                    <span style={{fontFamily: MONO, fontSize: 12, fontWeight: 600, flex: "0 0 auto"}}>{r.tag}</span>
                    {labelW > 100 && <span style={{fontSize: 11, color: "rgba(0,0,0,.6)", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0}}>{partner}</span>}
                </div>
            </th>
            {r.cells.map((c, j) => {
                const style = {...cellBase, minWidth: minW};
                if (c.kind === "grey") { style.background = c.bg; style.color = NEUTRAL.zeroDot; style.textAlign = "center"; }
                else if (c.kind === "hollow") { style.border = HOLLOW_BORDER; style.color = "rgba(0,0,0,.38)"; }
                else if (c.bg) style.background = c.bg;
                if (c.launch) style.outline = LAUNCH_BORDER; // outline, so it never shifts the 20 px grid
                if (c.launch) style.outlineOffset = -1;
                return <td key={j} data-j={j} style={style}>{c.text}</td>;
            })}
            <td style={{...cellBase, minWidth: minW + 8, fontWeight: 600, borderLeft: `1px solid ${NEUTRAL.divider}`}}>{r.totalText}</td>
        </tr>
    );
});

const INK = {low: "#b71c1c", high: "#1b5e20"};
const inkFor = (bg) => (isLow(bg) ? INK.low : isHigh(bg) ? INK.high : "rgba(0,0,0,.6)");

/**
 * Network row: the sum per day, tinted with the network rule; the label carries the last closed day's story
 * (`−$31 · 3↓ 1↑`, or the one tag that dragged it) so the folded 12-row map answers "which network moved, and why".
 */
const FAMILY_BG = "#ececec";   // opaque: the label column is sticky and must hide the cells scrolling under it
const GroupRow = React.memo(function GroupRow({g, minW, labelW, collapsed, money, phone, level = 2}) {
    const mv = moveText(g, money);
    const family = level === 1;
    const sub = family ? `${g.groups.length} partner${g.groups.length === 1 ? "" : "s"} · ${g.count} tag${g.count === 1 ? "" : "s"}` : `${g.count} tag${g.count === 1 ? "" : "s"}`;
    const title = `${g.label}${family && g.code && g.code !== g.label ? ` (${g.code})` : ""} · ${sub}${mv ? ` · last day ${mv}` : ""}`;
    const ink = inkFor(g.move ? g.move.bg : null);
    const h = phone ? CELL_H * 2 : CELL_H;
    const bg = family ? FAMILY_BG : NEUTRAL.headerBg;
    return (
        <tr data-group={g.key} style={{cursor: "pointer"}} aria-expanded={!collapsed} aria-level={level}>
            <th scope="row" role="button" tabIndex={0} data-group={g.key} title={title} className="hm-label" style={{
                ...labelBase, width: labelW, minWidth: labelW, maxWidth: labelW, height: h, background: bg, fontWeight: 600,
                borderTop: `1px solid ${NEUTRAL.divider}`, paddingLeft: family ? 6 : 14, fontSize: family ? 12 : 11.5,
            }}>
                {phone ? (
                    <div style={{fontSize: 11, lineHeight: "18px", overflow: "hidden"}}>
                        <div style={{display: "flex", alignItems: "baseline", gap: 4, overflow: "hidden", whiteSpace: "nowrap"}}>
                            <span aria-hidden="true" style={{color: "rgba(0,0,0,.46)", fontSize: 9, width: 8, flex: "0 0 auto"}}>{collapsed ? "▶" : "▼"}</span>
                            <span style={{overflow: "hidden", textOverflow: "ellipsis"}}>{g.label}</span>
                        </div>
                        <div style={{color: ink, fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingLeft: 12}}>{mv}</div>
                    </div>
                ) : (
                    <div style={{display: "flex", alignItems: "baseline", gap: 6, fontSize: 11.5, lineHeight: `${CELL_H}px`, overflow: "hidden"}}>
                        <span aria-hidden="true" style={{color: "rgba(0,0,0,.46)", fontSize: 9, width: 8, flex: "0 0 auto"}}>{collapsed ? "▶" : "▼"}</span>
                        <span style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{g.label}</span>
                        <span style={{color: "rgba(0,0,0,.6)", fontWeight: 400, flex: "0 0 auto"}}>{g.count}</span>
                        <span style={{fontVariantNumeric: "tabular-nums", flex: "0 0 auto", marginLeft: "auto", color: ink, fontSize: 11}}>{mv}</span>
                    </div>
                )}
            </th>
            {g.cells.map((c, j) => {
                const style = {...cellBase, minWidth: minW, background: c.bg || bg, fontWeight: 600, borderTop: `1px solid ${NEUTRAL.divider}`, height: h, lineHeight: `${h}px`};
                if (c.kind === "grey") { style.color = NEUTRAL.zeroDot; style.textAlign = "center"; }
                else if (c.kind === "hollow") { style.border = HOLLOW_BORDER; style.color = "rgba(0,0,0,.38)"; }
                return <td key={j} data-j={j} style={style}>{c.text}</td>;
            })}
            <td style={{...cellBase, minWidth: minW + 8, background: bg, fontWeight: 600, borderTop: `1px solid ${NEUTRAL.divider}`, borderLeft: `1px solid ${NEUTRAL.divider}`, height: h, lineHeight: `${h}px`}}>{g.totalText}</td>
        </tr>
    );
});

// ---------------------------------------------------------------------------------------------------------------------

const Swatch = ({bg, border, outline, label}) => (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{whiteSpace: "nowrap"}}>
        <Box aria-hidden="true" sx={{
            width: 12, height: 12, borderRadius: "2px", boxSizing: "border-box", bgcolor: bg || "transparent",
            border: border || `1px solid ${NEUTRAL.divider}`, outline: outline || "none", outlineOffset: -1,
        }}/>
        <span>{label}</span>
    </Stack>
);

const LEGEND = [
    {bg: HEAT.low2, label: "< 50%"},
    {bg: HEAT.low1, label: "< 80%"},
    {bg: null, label: "±20%"},
    {bg: HEAT.high1, label: "> 130%"},
    {bg: HEAT.high2, label: "> 200%"},
    {bg: GREY_BG, border: "1px solid transparent", label: "nothing sent"},
    {bg: null, border: HOLLOW_BORDER, label: "not reported"},
    {bg: null, border: "1px solid transparent", outline: LAUNCH_BORDER, label: "new tag"},
];
const GROUP_LEGEND = "channel / partner rows: ±12% / ±25% (sums are steadier) · N↓ N↑ = tags that moved on the last day";

/**
 * Heatmap of one metric per tag and day. `rows` are the page's filtered tag rows; `days` the closed-day columns; the
 * metric / colour mode / range preset are controlled by the page (`onMetric`, `onMode`, `onPreset`).
 */
const Heatmap = ({
    rows = NONE, days = NONE, metric = "rev", onMetric, mode = "own", onMode, preset = 7, onPreset,
    onSelectTag, selectedTag = null, launches = NONE, pendingProviders = NONE, groupLabel, phone = false,
    sheetOpen = false, onSheetClose,
}) => {
    const theme = useTheme();
    const m = metricMeta(metric);
    // Folded by default: the 12-row network map is the morning glance. The open set lives in sessionStorage per
    // preset (survives metric / tab switches, not a new day).
    const storeKey = `tc.hm.open.${preset}`;
    const [open, setOpenState] = useState(() => {
        try { const v = JSON.parse(window.sessionStorage.getItem(storeKey) || "null"); if (Array.isArray(v)) return new Set(v); } catch (e) { /* ignore */ }
        return new Set();
    });
    const setOpen = useCallback((updater) => {
        setOpenState((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            try { window.sessionStorage.setItem(storeKey, JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
            return next;
        });
    }, [storeKey]);
    const [tip, setTip] = useState(null);
    const [showZero, setShowZero] = useState(false);
    const wrapRef = useRef(null);
    const scrollRef = useRef(null);
    // Phone: start scrolled to the right so the last closed day (the one that matters) and Σ are on screen; the label column is
    // sticky. Keyed on the CONTENT of `days` (a refetch builds a new array) so a poll never yanks the user back to the right;
    // layout effect so the first paint is already on the right.
    const lastDay = days[days.length - 1];
    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (phone && el) el.scrollLeft = el.scrollWidth;
    }, [phone, lastDay, days.length, metric]);
    // Phone: the grid box is an x-scroller with overflow-y hidden, which makes it a scroll container of its own — so a
    // `position: sticky; top` header INSIDE it can never stick to the page. A clone of the header row sits in a sticky strip
    // over the real one (same widths, same x offset), stuck under the tab row while the page scrolls.
    const headRef = useRef(null);
    useLayoutEffect(() => {
        if (!phone) return undefined;
        const box = scrollRef.current, clone = headRef.current;
        if (!box || !clone) return undefined;
        const onScroll = () => { clone.scrollLeft = box.scrollLeft; };
        box.addEventListener("scroll", onScroll, {passive: true});
        return () => box.removeEventListener("scroll", onScroll);
    }, [phone]);
    useLayoutEffect(() => {
        if (!phone) return;
        const box = scrollRef.current, clone = headRef.current;
        if (!box || !clone) return;
        const src = box.querySelectorAll("thead th"), dst = clone.querySelectorAll("thead th");
        src.forEach((th, i) => {
            const d = dst[i];
            if (!d) return;
            const w = `${th.offsetWidth}px`;
            d.style.width = w; d.style.minWidth = w; d.style.maxWidth = w;
        });
        const t = clone.querySelector("table");
        if (t) t.style.width = `${box.querySelector("table").offsetWidth}px`;
        clone.scrollLeft = box.scrollLeft;
    });

    const model = useMemo(
        () => buildModel(rows, days, m, mode, pendingProviders, launches, groupLabel),
        [rows, days, m, mode, pendingProviders, launches, groupLabel],
    );
    const rowByTag = useMemo(() => {
        const map = new Map();
        model.groups.forEach((g) => g.rows.forEach((r) => map.set(r.tag, r)));
        return map;
    }, [model]);
    const groupByKey = useMemo(() => new Map([...model.families, ...model.groups].map((g) => [g.key, g])), [model]);

    const paper = theme.palette.background.paper;
    const sevColors = {error: SEV.error.dot(theme), warning: SEV.warning.dot(theme)};
    const minW = phone ? 22 : 28;
    const labelW = phone ? 96 : 190;   // phone: the tag only (the group row already names the network)
    const baseWord = mode === "d1" ? "day before" : "7d avg";

    const toggleGroup = useCallback((prefix) => {
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(prefix)) next.delete(prefix); else next.add(prefix);
            return next;
        });
    }, [setOpen]);
    // A single family left by the page filter opens itself (and its single partner): a one-row folded map is pointless.
    const single = new Set();
    if (model.families.length === 1) { single.add(model.families[0].key); if (model.families[0].groups.length === 1) single.add(model.families[0].groups[0].key); }
    const allKeys = useMemo(() => [...model.families, ...model.groups].map((g) => g.key), [model]);
    const movedKeys = useMemo(() => [...model.families.filter((f) => f.moved), ...model.groups.filter((g) => g.moved && model.families.find((f) => f.key === `f:${g.family}` && f.moved))].map((g) => g.key), [model]);
    const openMode = allKeys.length && open.size === 0 ? "none"
        : allKeys.every((k) => open.has(k)) ? "all"
        : movedKeys.length && movedKeys.every((k) => open.has(k)) && open.size === movedKeys.length ? "moved" : null;
    const openControl = (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{whiteSpace: "nowrap"}}>
            {!phone && <Typography component="span" sx={{fontSize: 11, fontWeight: 600, color: "text.secondary"}}>Open</Typography>}
            <ToggleButtonGroup exclusive size="small" value={openMode} aria-label="Networks open"
                               onChange={(e, v) => { if (v === "none") setOpen(new Set()); else if (v === "all") setOpen(new Set(allKeys)); else if (v === "moved") setOpen(new Set(movedKeys)); }}
                               sx={toggleSx(phone ? 24 : 22)}>
                <ToggleButton value="none">none</ToggleButton>
                <ToggleButton value="moved" aria-label={`Open the ${model.families.filter((f) => f.moved).length} channels that moved`}>moved{model.families.filter((f) => f.moved).length ? ` · ${model.families.filter((f) => f.moved).length}` : ""}</ToggleButton>
                <ToggleButton value="all">all</ToggleButton>
            </ToggleButtonGroup>
        </Stack>
    );

    // One delegated click / key handler for the whole grid: tag rows select, network rows fold.
    const activate = useCallback((el) => {
        const tr = el && el.closest ? el.closest("tr[data-tag], tr[data-group]") : null;
        if (!tr) return;
        if (tr.dataset.tag) { if (onSelectTag) onSelectTag(tr.dataset.tag); }
        else if (tr.dataset.group) toggleGroup(tr.dataset.group);
    }, [onSelectTag, toggleGroup]);
    const onClick = useCallback((e) => activate(e.target), [activate]);
    const onKeyDown = useCallback((e) => {
        if ((e.key === "Enter" || e.key === " ") && e.target.classList && e.target.classList.contains("hm-label")) {
            e.preventDefault();
            activate(e.target);
        }
    }, [activate]);

    // One floating tooltip for the whole grid: mouseover on the table, positioned against the outer wrapper.
    const onMouseOver = useCallback((e) => {
        const td = e.target && e.target.closest ? e.target.closest("td[data-j]") : null;
        if (!td || !wrapRef.current) { setTip((t) => (t ? null : t)); return; }
        const tr = td.parentElement;
        const j = Number(td.dataset.j);
        const day = days[j];
        const lines = [];
        if (tr.dataset.tag) {
            const r = rowByTag.get(tr.dataset.tag);
            const c = r && r.cells[j];
            if (!c) return;
            lines.push(`${r.tag} · ${fmtDay(day)} ${fmtDow(day)}`);
            if (c.kind === "grey") {
                lines.push(`${m.short} ${c.v === null ? EMPTY : m.fmt(c.v)}`);
                lines.push("nothing sent, nothing earned");
            } else if (c.kind === "hollow") {
                lines.push(`${m.short} ${c.v === null ? EMPTY : m.fmt(c.v)}`);
                lines.push("not yet reported by the partner");
            } else {
                const delta = c.base !== null && c.base > 0 && c.v !== null ? fmtSignedPct((c.v / c.base - 1) * 100) : EMPTY;
                lines.push(`${m.short} ${c.v === null ? EMPTY : m.fmt(c.v)} · ${baseWord} ${c.base === null ? EMPTY : m.fmt(c.base)} · ${delta}`);
            }
        } else if (tr.dataset.group) {
            const g = groupByKey.get(tr.dataset.group);
            const c = g && g.cells[j];
            if (!c) return;
            lines.push(`${g.label} · ${fmtDay(day)} ${fmtDow(day)}`);
            if (c.kind === "grey") lines.push("nothing sent, nothing earned");
            else if (c.kind === "hollow") lines.push(`${m.short} ${c.text} · a partner has not reported this day yet`);
            else {
                const delta = c.base !== null && c.base > 0 && c.v !== null ? fmtSignedPct((c.v / c.base - 1) * 100) : EMPTY;
                lines.push(`${m.short} ${c.text} · ${mode === "d1" ? "day before" : "7d median"} ${c.base === null ? EMPTY : m.fmt(c.base)} · ${delta}`);
            }
            if (j === days.length - 1 && g.move) {
                const mv = g.move;
                const fmtT = (t) => `${t.tag} ${t.usd !== null ? fmtSignedMoney(t.usd) : t.pct !== null ? fmtSignedPct(t.pct) : ""}`.trim();
                const downs = [...mv.down, ...mv.dark];
                lines.push(`${g.count} tags · ${downs.length} down${downs.length ? ` (${downs.slice(0, 3).map(fmtT).join(", ")}${downs.length > 3 ? ", …" : ""})` : ""} · ${mv.up.length} up${mv.up.length ? ` (${mv.up.slice(0, 2).map(fmtT).join(", ")}${mv.up.length > 2 ? ", …" : ""})` : ""}`);
            }
        } else return;
        const wr = wrapRef.current.getBoundingClientRect();
        const cr = td.getBoundingClientRect();
        const x = Math.min(Math.max(cr.left - wr.left + cr.width / 2, 80), Math.max(80, wr.width - 80));
        const above = cr.top - wr.top > 70;
        setTip({x, y: above ? cr.top - wr.top - 4 : cr.bottom - wr.top + 4, above, lines});
    }, [days, rowByTag, groupByKey, m, baseWord, mode]);
    const hideTip = useCallback(() => setTip(null), []);

    const count = `${model.families.length} channels · ${model.groups.length} partners · ${rows.length} tag${rows.length === 1 ? "" : "s"} × ${days.length} day${days.length === 1 ? "" : "s"}`;
    const btnH = phone ? 24 : 26;

    const metricToggle = (
        <ToggleButtonGroup exclusive size="small" value={m.key} onChange={(e, v) => v && onMetric && onMetric(v)} aria-label="Heatmap metric" sx={toggleSx(btnH)}>
            {HEAT_METRICS.map((x) => <ToggleButton key={x.key} value={x.key} aria-label={`Paint ${x.short}`}>{x.short}</ToggleButton>)}
        </ToggleButtonGroup>
    );
    const modeToggle = (
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(e, v) => v && onMode && onMode(v)} aria-label="Colour base" sx={toggleSx(btnH)}>
            {MODES.map((x) => <ToggleButton key={x.key} value={x.key}>{x.label}</ToggleButton>)}
        </ToggleButtonGroup>
    );
    const daysToggle = (
        <ToggleButtonGroup exclusive size="small" value={preset} onChange={(e, v) => v && onPreset && onPreset(v)} aria-label="Days shown" sx={toggleSx(btnH)}>
            {PRESETS.map((p) => <ToggleButton key={p.key} value={p.key} aria-label={p.long}>{p.label}</ToggleButton>)}
        </ToggleButtonGroup>
    );

    const headTh = {
        position: "sticky", top: 0, zIndex: 3, background: paper, height: 34, boxSizing: "border-box", padding: "2px 3px",
        fontSize: 11, fontWeight: 600, lineHeight: "15px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top",
        borderBottom: `1px solid ${NEUTRAL.divider}`, fontVariantNumeric: "tabular-nums",
    };

    const tableStyle = {borderCollapse: "separate", borderSpacing: 0, width: "max-content", minWidth: "100%", ...TABULAR};
    const headRow = (
                        <tr>
                            <th scope="col" style={{...headTh, left: 0, zIndex: 4, textAlign: "left", padding: "2px 6px", width: labelW, minWidth: labelW, maxWidth: labelW, borderRight: `1px solid ${NEUTRAL.divider}`}}>
                                {phone ? (
                                    <div role="button" tabIndex={0} aria-label="Cycle open networks: none, moved, all"
                                         onClick={(e) => { e.stopPropagation(); const next = openMode === "none" ? "moved" : openMode === "moved" ? "all" : "none"; if (next === "none") setOpen(new Set()); else if (next === "all") setOpen(new Set(allKeys)); else setOpen(new Set(movedKeys)); }}
                                         style={{color: "#1976d2", fontWeight: 600, cursor: "pointer", lineHeight: "15px"}}>
                                        {openMode === "all" ? "▾ all" : openMode === "moved" ? `▾ moved · ${model.families.filter((f) => f.moved).length}` : "▸ open"}
                                    </div>
                                ) : <div>{`${rows.length} tags`}</div>}
                                <div style={{fontWeight: 400, color: "rgba(0,0,0,.6)"}}>{m.short}</div>
                            </th>
                            {days.map((day, j) => {
                                const t = model.totals[j];
                                const tip = `${fmtDay(day)} ${fmtDow(day)}${t.pending ? " · a partner has not reported yet" : t.base ? ` · ${mode === "d1" ? "day before" : "7d median"} ${m.fmt(t.base)}` : ""}`;
                                return (
                                    <th key={day} scope="col" title={tip} style={{...headTh, minWidth: minW, background: t.bg || paper}}>
                                        <div>{fmtDay(day)}</div>
                                        <div style={{fontWeight: 600, color: inkFor(t.bg)}}>{t.text}</div>
                                    </th>
                                );
                            })}
                            <th scope="col" title="Range total" style={{...headTh, minWidth: minW + 8, borderLeft: `1px solid ${NEUTRAL.divider}`}}>
                                <div>Σ</div>
                                <div style={{fontWeight: 400, color: "rgba(0,0,0,.6)"}}>{model.grandTotalText}</div>
                            </th>
                        </tr>
    );

    return (
        <Box component="section" aria-label="Heatmap" sx={{minWidth: 0}}>
            {/* Phone: no toolbar — the settings live in a bottom sheet opened from the tab row; Open cycles in the grid corner. */}
            {phone ? (
                <Drawer anchor="bottom" open={sheetOpen} onClose={onSheetClose} PaperProps={{sx: {borderTopLeftRadius: 16, borderTopRightRadius: 16, px: 2, pt: 1.5, pb: 2}}}>
                    <Box aria-hidden="true" sx={{width: 32, height: 4, borderRadius: 2, bgcolor: "grey.300", mx: "auto", mb: 1.5}}/>
                    <Stack direction="row" alignItems="center" sx={{mb: 1}}>
                        <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em"}}>Heatmap</Typography>
                        <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", ...TABULAR, ml: 1, minWidth: 0}}>{count}</Typography>
                        <Box sx={{ml: "auto"}}><HelpPopover label="Heatmap" lines={HEATMAP_HELP}/></Box>
                    </Stack>
                    {[["Metric", metricToggle], ["Colour", modeToggle], ["Days", daysToggle], ["Open", openControl]].map(([label, ctl]) => (
                        <Stack key={label} direction="row" alignItems="center" sx={{minHeight: 44, gap: 1}}>
                            <Typography component="span" sx={{...TYPE.eyebrow, width: 56, flex: "0 0 auto"}}>{label}</Typography>
                            <Box sx={{minWidth: 0, flex: "1 1 auto", "& .MuiToggleButtonGroup-root": {flexWrap: "wrap"}}}>{ctl}</Box>
                        </Stack>
                    ))}
                </Drawer>
            ) : (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 0.75, rowGap: 0.75}}>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{minWidth: 0, mr: "auto"}}>
                    <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap"}}>Heatmap</Typography>
                    <Typography component="span" noWrap sx={{fontSize: 12.5, color: "text.secondary", ...TABULAR}}>{count}</Typography>
                </Stack>
                {metricToggle}
                {modeToggle}
                {daysToggle}
                <HelpPopover label="Heatmap" lines={HEATMAP_HELP}/>
            </Stack>
            )}

            {phone ? null : (
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{mb: 0.75, fontSize: 11, color: "text.secondary", rowGap: 0.5}} aria-label="Legend">
                    {openControl}
                    <Box component="span" sx={{fontWeight: 600, ml: 1}}>{mode === "d1" ? "vs the day before:" : "vs its own 7-day avg:"}</Box>
                    {LEGEND.map((l) => <Swatch key={l.label} {...l}/>)}
                    <Box component="span" title={GROUP_LEGEND} sx={{color: "text.disabled"}}>· {GROUP_LEGEND}</Box>
                </Stack>
            )}

            {rows.length === 0 ? (
                <Typography sx={{...TYPE.muted, py: 3, textAlign: "center"}}>No tags to paint</Typography>
            ) : (
                <Box ref={wrapRef} sx={{position: "relative", minWidth: 0, isolation: "isolate"}} onMouseLeave={hideTip}>
                    {phone && (
                        <Box ref={headRef} sx={{
                            position: "sticky", top: PHONE_STACK, zIndex: 5, height: 35, mb: "-35px", boxSizing: "border-box", overflow: "hidden",
                            bgcolor: "background.paper", border: `1px solid ${NEUTRAL.divider}`, borderBottom: "none", borderRadius: "6px 6px 0 0",
                        }}>
                            <table aria-hidden="true" style={{...tableStyle, tableLayout: "fixed"}}>
                                <thead>{headRow}</thead>
                            </table>
                        </Box>
                    )}
                    <Box ref={scrollRef} sx={{
                        // Phone: the grid must never be a vertical scroll box. iOS Safari (WebKit) turned the x-scroller into a
                        // clipped y-scroller too ("page stops after 20 px"): with overflow-y hidden the box is as tall as the
                        // table, vertical swipes go to the page, only the x axis scrolls inside.
                        overflowX: "auto", overflowY: phone ? "hidden" : "auto", maxHeight: phone ? "none" : "calc(100vh - 260px)",
                        border: `1px solid ${NEUTRAL.divider}`, borderRadius: "6px", bgcolor: "background.paper",
                        "& .hm-label:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing, zIndex: 3},
                        "@media (hover: hover)": {"& tr[data-tag]:hover td, & tr[data-tag]:hover th": {filter: "brightness(0.96)"}},
                    }}>
                        <table aria-label={`Heatmap of ${m.short} per tag and day`} onClick={onClick} onKeyDown={onKeyDown} onMouseOver={phone ? undefined : onMouseOver}
                               style={tableStyle}>
                            <thead>
                                {headRow}
                            </thead>
                            <tbody>
                                {model.families.filter((f) => showZero || f.rev > 0 || f.alarms > 0).map((f) => {
                                    const fFold = !(open.has(f.key) || single.has(f.key));
                                    const zero = f.groups.filter((g) => !(g.rev > 0 || g.alarms > 0));
                                    const shownGroups = f.groups.filter((g) => showZero || g.rev > 0 || g.alarms > 0);
                                    return (
                                        <React.Fragment key={f.key}>
                                            <GroupRow g={f} minW={minW} labelW={labelW} collapsed={fFold} money={MONEY_METRICS.has(m.key)} phone={phone} level={1}/>
                                            {!fFold && shownGroups.map((g) => {
                                                const fold = !(open.has(g.key) || single.has(g.key));
                                                return (
                                                    <React.Fragment key={g.key}>
                                                        <GroupRow g={g} minW={minW} labelW={labelW} collapsed={fold} money={MONEY_METRICS.has(m.key)} phone={phone} level={2}/>
                                                        {!fold && g.rows.map((r) => (
                                                            <TagRow key={r.tag} r={r} minW={minW} labelW={labelW} paper={paper}
                                                                    selected={selectedTag === r.tag} sevColor={sevColors[r.sev] || null}/>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {!fFold && zero.length > 0 && !showZero && (
                                                <tr>
                                                    <td colSpan={days.length + 2} onClick={(e) => { e.stopPropagation(); setShowZero(true); }} style={{...cellBase, textAlign: "left", padding: "0 6px 0 14px", color: "rgba(0,0,0,.6)", cursor: "pointer", background: NEUTRAL.headerBg, position: "sticky", left: 0}}>
                                                        {zero.length} partner{zero.length === 1 ? "" : "s"} with $0 in the range ({zero.map((g) => g.label).slice(0, 6).join(", ")}) · <span style={{color: "#1976d2", fontWeight: 600}}>show</span>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {!showZero && model.families.some((f) => !(f.rev > 0 || f.alarms > 0)) && (
                                    <tr>
                                        <td colSpan={days.length + 2} onClick={(e) => { e.stopPropagation(); setShowZero(true); }} style={{...cellBase, textAlign: "left", padding: "0 6px", color: "rgba(0,0,0,.6)", cursor: "pointer", background: FAMILY_BG, borderTop: `1px solid ${NEUTRAL.divider}`, position: "sticky", left: 0}}>
                                            {model.families.filter((f) => !(f.rev > 0 || f.alarms > 0)).length} channels with $0 in the range ({model.families.filter((f) => !(f.rev > 0 || f.alarms > 0)).map((f) => f.label).join(", ")}) · <span style={{color: "#1976d2", fontWeight: 600}}>show</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </Box>
                    {tip && (
                        <Box role="tooltip" sx={{
                            position: "absolute", left: tip.x, top: tip.y, transform: tip.above ? "translate(-50%, -100%)" : "translate(-50%, 0)",
                            zIndex: 5, pointerEvents: "none", bgcolor: "background.paper", border: `1px solid ${NEUTRAL.divider}`,
                            borderRadius: 1, px: 1, py: 0.5, fontSize: 11.5, ...TABULAR, boxShadow: 1, whiteSpace: "nowrap", lineHeight: 1.4,
                        }}>
                            {tip.lines.map((l, i) => <div key={i} style={{fontWeight: i === 0 ? 600 : 400}}>{l}</div>)}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default Heatmap;
